import { LoadingBlock, LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select } from "@normasteaching/norma-ui";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  HistoryGranularity,
  HistoryPeriodDetailDTO,
  HistoryPeriodsDTO,
  useGetHubSpotCloserAvailabilityHistory,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import { CheckClosersAvailabilityScreenPath } from "../CheckClosersAvailabilityScreen.tsx";
import {
  deltaPct,
  filterByTeam,
  formatDelta,
  formatPct,
  formatTakenAt,
  NO_TEAM_LABEL,
  periodLabel,
  saturation,
  sumCloserDays,
  sumTotals,
  sumTransitions,
} from "./closerAvailabilityHistoryStats.ts";
import { HistoryTrendChart } from "./HistoryTrendChart.tsx";
import { HistoryCloserTable } from "./HistoryCloserTable.tsx";
import { HistoryDayTable } from "./HistoryDayTable.tsx";
import { HistoryChangesPanel } from "./HistoryChangesPanel.tsx";
import { CloserHistoryDetailModal } from "./CloserHistoryDetailModal.tsx";

type Tab = "closers" | "days" | "changes";

const TREND_TITLE: Record<HistoryGranularity, string> = {
  day: "ultimi 30 giorni",
  week: "ultime 12 settimane",
  month: "ultimi 12 mesi",
};

const GRANULARITY_LABEL: Record<HistoryGranularity, string> = {
  day: "Giorno",
  week: "Settimana",
  month: "Mese",
};

function defaultPeriodStart(history: HistoryPeriodsDTO): string | null {
  const periods = history.periods;
  const closed = [...periods].reverse().find((p) => p.closureTakenAt);
  const opened = [...periods].reverse().find((p) => p.openingTakenAt);
  return (
    closed?.periodStart ??
    opened?.periodStart ??
    periods[periods.length - 1]?.periodStart ??
    null
  );
}

function Kpi({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: ReactNode;
  sub: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={classNames(
        "rounded-lg p-4 flex flex-col gap-1",
        highlight ? "bg-primary text-white" : "bg-white",
      )}
    >
      <span
        className={classNames(
          "text-xs font-medium",
          highlight ? "text-blue-200" : "text-gray-500",
        )}
      >
        {label}
      </span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      <span
        className={classNames(
          "text-xs",
          highlight ? "text-blue-200" : "text-gray-400",
        )}
      >
        {sub}
      </span>
    </div>
  );
}

export function CloserAvailabilityHistoryScreen() {
  const navigate = useNavigate();
  const { getHistoryPeriods, getHistoryPeriodDetail } =
    useGetHubSpotCloserAvailabilityHistory();

  const [granularity, setGranularity] = useState<HistoryGranularity>("week");
  const [team, setTeam] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryPeriodsDTO | null>(null);
  const [periodStart, setPeriodStart] = useState<string | null>(null);
  const [detail, setDetail] = useState<HistoryPeriodDetailDTO | null>(null);
  const [tab, setTab] = useState<Tab>("closers");
  const [selectedCloser, setSelectedCloser] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detailRequest = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setHistory(null);
    setDetail(null);
    setPeriodStart(null);
    setLoadingHistory(true);
    setError(null);
    getHistoryPeriods(granularity)
      .then((data) => {
        if (cancelled) return;
        setHistory(data);
        setPeriodStart(defaultPeriodStart(data));
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setError("Impossibile caricare lo storico.");
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [granularity, getHistoryPeriods]);

  useEffect(() => {
    if (!periodStart) return;
    const request = ++detailRequest.current;
    setLoadingDetail(true);
    setError(null);
    getHistoryPeriodDetail(granularity, periodStart)
      .then((data) => {
        if (request === detailRequest.current) setDetail(data);
      })
      .catch((e) => {
        console.error(e);
        if (request === detailRequest.current)
          setError("Impossibile caricare il dettaglio del periodo.");
      })
      .finally(() => {
        if (request === detailRequest.current) setLoadingDetail(false);
      });
  }, [granularity, periodStart, getHistoryPeriodDetail]);

  useEffect(() => {
    if (granularity === "month" && tab === "changes") setTab("closers");
    if (granularity === "day" && tab === "days") setTab("closers");
  }, [granularity, tab]);

  const periods = useMemo(() => history?.periods ?? [], [history]);
  const periodIndex = periods.findIndex((p) => p.periodStart === periodStart);
  const currentPeriod = periodIndex >= 0 ? periods[periodIndex] : null;
  const teams = history?.teams ?? [];
  const teamOptions = [...teams, NO_TEAM_LABEL].map((t) => ({
    id: t,
    name: t,
  }));

  const closers = useMemo(
    () => filterByTeam(detail?.closers ?? [], team),
    [detail, team],
  );
  const hasClosure = detail?.closureTakenAt != null;
  const comparable = hasClosure && detail?.openingTakenAt != null;
  const totals = sumTotals(closers);
  const transitions = sumTransitions(closers);
  const delta = totals.closure - totals.opening;
  const days =
    granularity === "month" ? (detail?.days ?? []) : sumCloserDays(closers);
  const detailCloser =
    detail?.closers.find((c) => c.email === selectedCloser) ?? null;

  return (
    <Layout maxWClass="max-w-[1280px]">
      <CloserHistoryDetailModal
        closer={detailCloser}
        periods={periods}
        addedBlocks={detail?.addedBlocks ?? null}
        hasClosure={hasClosure}
        comparable={comparable}
        onClose={() => setSelectedCloser(null)}
      />

      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-2">
              <button
                onClick={() => navigate(CheckClosersAvailabilityScreenPath)}
                className="text-sm text-primary hover:underline"
              >
                ← Check Closer Availability
              </button>
              <h1 className="font-bold text-2xl">
                Storico disponibilità closer
              </h1>
              {currentPeriod && (
                <p className="text-sm text-gray-500">
                  Apertura: {formatTakenAt(currentPeriod.openingTakenAt)} ·
                  Chiusura: {formatTakenAt(currentPeriod.closureTakenAt)}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Periodo</p>
                <div className="flex gap-1">
                  {(["day", "week", "month"] as const).map((g) => (
                    <button
                      key={g}
                      onClick={() => setGranularity(g)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        granularity === g
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {GRANULARITY_LABEL[g]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-48 space-y-1">
                <p className="text-sm font-medium text-gray-700">Team</p>
                <Select
                  clearable
                  placeholder="Tutti i team"
                  value={team ?? undefined}
                  onChange={(t) => setTeam(t)}
                  options={teamOptions}
                />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">
                  {GRANULARITY_LABEL[granularity]}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    disabled={periodIndex <= 0}
                    onClick={() =>
                      setPeriodStart(periods[periodIndex - 1].periodStart)
                    }
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md text-sm disabled:text-gray-300"
                    aria-label="Periodo precedente"
                  >
                    ‹
                  </button>
                  <span className="min-w-[170px] text-center font-semibold tabular-nums">
                    {currentPeriod
                      ? periodLabel(granularity, currentPeriod)
                      : "—"}
                  </span>
                  <button
                    disabled={
                      periodIndex < 0 || periodIndex >= periods.length - 1
                    }
                    onClick={() =>
                      setPeriodStart(periods[periodIndex + 1].periodStart)
                    }
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md text-sm disabled:text-gray-300"
                    aria-label="Periodo successivo"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}
        </Card>

        {detail && (
          <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
            <Kpi
              label="Capacity apertura"
              value={detail.openingTakenAt ? totals.opening : "—"}
              sub="liberi + in call all'apertura"
            />
            <Kpi
              highlight
              label="Slot effettivi chiusura"
              value={hasClosure ? totals.closure : "—"}
              sub={
                comparable ? (
                  <>
                    <span
                      className={
                        delta < 0
                          ? "text-red-300"
                          : delta > 0
                            ? "text-green-300"
                            : ""
                      }
                    >
                      {formatDelta(delta)} (
                      {formatPct(deltaPct(totals.closure, totals.opening))})
                    </span>{" "}
                    rispetto all'apertura
                  </>
                ) : hasClosure ? (
                  "nessuna apertura con cui confrontare"
                ) : (
                  "chiusura non ancora disponibile"
                )
              }
            />
            <Kpi
              label="Call prenotate"
              value={hasClosure ? totals.calls : "—"}
              sub={`saturazione ${saturation(totals.calls, totals.closure)}%`}
            />
            {granularity !== "month" ? (
              <Kpi
                label="Slot bloccati dopo l'apertura"
                value={
                  <span className="text-red-600">
                    {transitions.blocked + transitions.callBlocked}
                  </span>
                }
                sub={`${transitions.callBlocked} erano call`}
              />
            ) : (
              <Kpi
                label="Slot OOO in chiusura"
                value={hasClosure ? totals.ooo : "—"}
                sub="blocchi ed eventi non di call"
              />
            )}
            <Kpi
              label="Closer entrati / usciti"
              value={comparable ? `${totals.joined} / ${totals.left}` : "—"}
              sub="rispetto all'apertura"
            />
          </div>
        )}

        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="font-semibold">
                Andamento {TREND_TITLE[granularity]}
              </h2>
              <p className="text-xs text-gray-400">
                Clicca un periodo per vederne il dettaglio.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#C7D5F5]" />
                Capacity apertura
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-primary" />
                Slot effettivi chiusura
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-600" />
                Δ %
              </span>
            </div>
          </div>
          {loadingHistory ? (
            <LoadingBlock sizeClassName={"h-56 w-full"} />
          ) : (
            <HistoryTrendChart
              granularity={granularity}
              periods={periods}
              team={team}
              selectedPeriodStart={periodStart}
              onSelect={setPeriodStart}
            />
          )}
        </Card>

        <Card>
          <div className="flex flex-wrap gap-1 border-b border-gray-200 -mx-4 px-3 -mt-1">
            {(
              [
                ["closers", "Per closer"],
                ...(granularity !== "day"
                  ? [["days", "Per giorno"] as [Tab, string]]
                  : []),
                ...(granularity !== "month"
                  ? [["changes", "Cosa è cambiato"] as [Tab, string]]
                  : []),
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-2.5 py-3 text-sm font-medium border-b-2 -mb-px ${
                  tab === key
                    ? "text-primary border-primary"
                    : "text-gray-500 border-transparent hover:text-gray-900"
                }`}
              >
                {label}
                {key === "changes" && detail?.addedBlocks && (
                  <span className="ml-1.5 text-[11px] rounded-full px-1.5 bg-gray-100 text-gray-500">
                    {
                      detail.addedBlocks.filter((b) =>
                        closers.some((c) => c.email === b.email),
                      ).length
                    }
                  </span>
                )}
              </button>
            ))}
          </div>

          {loadingDetail || !detail ? (
            loadingDetail || loadingHistory ? (
              <LoadingRows rows={6} />
            ) : (
              <p className="text-sm text-gray-400">
                Nessun periodo disponibile.
              </p>
            )
          ) : (
            <>
              {tab === "closers" && (
                <HistoryCloserTable
                  closers={closers}
                  teams={teams}
                  hasClosure={hasClosure}
                  comparable={comparable}
                  showLost={granularity !== "month"}
                  onSelectCloser={setSelectedCloser}
                />
              )}
              {tab === "days" && (
                <div className="space-y-2">
                  {granularity === "month" && team !== null && (
                    <p className="text-xs text-gray-400">
                      Per il mese i dati per giorno sono il totale di tutti i
                      team.
                    </p>
                  )}
                  <HistoryDayTable
                    days={days}
                    hasClosure={hasClosure}
                    comparable={comparable}
                  />
                </div>
              )}
              {tab === "changes" && detail.addedBlocks && (
                <HistoryChangesPanel
                  closers={closers}
                  addedBlocks={detail.addedBlocks}
                  comparable={comparable}
                />
              )}
            </>
          )}
        </Card>

        <p className="text-xs text-gray-400">
          Slot effettivi = slot liberi + slot in call. Capacity apertura =
          stesso calcolo sulla rilevazione di apertura (00:05 del giorno stesso
          per il giorno, giovedì precedente alle 12:00 per la settimana, 1° del
          mese per il mese). La chiusura del giorno è fatta alle 00:05 del
          giorno dopo. Saturazione = call / slot effettivi.
        </p>
      </div>
    </Layout>
  );
}
